/**
 * Tracking Service
 *
 * Provides a centralized service for tracking events via HubSpot's tracking queue.
 * This service ensures consistent tracking across the application.
 */
import { useAppStore } from '@/store/app';

class HsTrackingService {
    /**
     * Track a page view
     * @param {string} pageName - Name of the page being viewed
     * @param {Object} properties - Additional page properties
     */
    static trackPageView(pageName, properties = {}) {
        return this.track('page_view', {
            page_name: pageName,
            ...properties,
        });
    }

    /**
     * Track user interaction events
     * @param {string} action - The action performed (click, submit, etc.)
     * @param {string} element - The element interacted with
     * @param {Object} properties - Additional interaction properties
     */
    static trackInteraction(action, element, properties = {}) {
        return this.track('user_interaction', {
            action,
            element,
            ...properties,
        });
    }

    /**
     * Track form submissions
     * @param {string} formName - Name of the form submitted
     * @param {Object} properties - Form-specific properties
     */
    static trackFormSubmission(formName, properties = {}) {
        return this.track('form_submission', {
            form_name: formName,
            ...properties,
        });
    }

    /**
     * Track conversion events
     * @param {string} conversionType - Type of conversion
     * @param {number} value - Monetary value of the conversion (optional)
     * @param {Object} properties - Additional conversion properties
     */
    static trackConversion(conversionType, value = null, properties = {}) {
        const options = value !== null ? { value } : {};

        return this.track(
            'conversion',
            {
                conversion_type: conversionType,
                ...properties,
            },
            options
        );
    }

    /**
     * Track custom business events
     * @param {string} trackingName - Name of the event
     * @param {Object} properties - Event properties
     */
    static trackCustomEvent(trackingName, properties = {}) {
        const appStore = useAppStore();
        const loggedInUser = appStore.userSettings;
        const userFirstName = loggedInUser
            ? loggedInUser.name.split(' ')[0]
            : null;
        const userLastName = loggedInUser
            ? loggedInUser.name.split(' ')[1]
            : null;
        const uuid = loggedInUser ? loggedInUser.uuid : null;

        return window._hsq.push([
            'trackCustomBehavioralEvent',
            {
                name: trackingName,
                properties: {
                    email: loggedInUser.email,
                    firstname: userFirstName,
                    lastname: userLastName,
                    my10x_user_uuid: uuid,
                    ...properties,
                },
            },
        ]);
    }
}

export default HsTrackingService;

// Also export individual methods for convenience
export const {
    trackPageView,
    trackInteraction,
    trackFormSubmission,
    trackConversion,
    trackCustomEvent,
} = HsTrackingService;
